import { AppThunk } from '../../../store/reducers';
import { DELETE, GET, POST, PUT } from '../../../service/HttpMethod';
import { InitialState, ActionConstant, ActionType, Lists, Plan } from './types';
import { toastFunction } from '../../../helpers/toastShow';
import { apiErrorMessage } from '../../../helpers/apiErrorMessage';

const PLAN_REQUEST_TIMEOUT_MS = 15000;

const initialState: InitialState = {
	count: 0,
	list: [],
	listFetching: true,
	statusCode: 0,
	planlist: [],
	plan: {
		id: '',
		name: '',
		description: '',
		documentCount: '',
		documentSize: '',
		scanCount: '',
		projectCount: '',
		tradeCount: '',
		userCount: '',
		duration: '',
		durationUnit: 0,
		amount: '',
	},
	detailsFetching: true,
};

const GetDetailsAction = (data: Lists): ActionType => ({
	type: ActionConstant.GET_PLAN_LIST,
	data,
});
const GetPlanDetailsAction = (data: Plan): ActionType => ({
	type: ActionConstant.GET_PLAN_DETAILS,
	data,
});
export const clearDetails = (): ActionType => ({ type: ActionConstant.CLEAR_DETAILS });

export const clearPlanDetails = (): ActionType => ({ type: ActionConstant.CLEAR_PLAN_DETAILS });

export const clearStatus = (): ActionType => ({ type: ActionConstant.CLEAR_STATUS });
export const planListFailed = (): ActionType => ({ type: ActionConstant.GET_PLAN_LIST_FAILED });
export const planDetailsFailed = (): ActionType => ({
	type: ActionConstant.GET_PLAN_DETAILS_FAILED,
});

export const getDetails = (): AppThunk => {
	// plan details
	const getConfig = { url: `plan`, appConfig: { timeout: PLAN_REQUEST_TIMEOUT_MS } };
	return async (dispatch) => {
		try {
			const { data = {} } = await GET(getConfig);
			dispatch(GetDetailsAction(data.data));
		} catch (error) {
			dispatch(planListFailed());
			dispatch(
				toastFunction('Error', apiErrorMessage(error, 'Failed to load subscription plans'), ''),
			);
		}
	};
};

export const getPlanDetails = (planId: number): AppThunk => {
	// plan details
	const getConfig = { url: `plan/${planId}`, appConfig: { timeout: PLAN_REQUEST_TIMEOUT_MS } };
	return async (dispatch) => {
		try {
			const { data = {} } = await GET(getConfig);
			dispatch(GetPlanDetailsAction(data.data));
		} catch (error) {
			dispatch(planDetailsFailed());
			dispatch(toastFunction('Error', apiErrorMessage(error, 'Failed to load plan details'), ''));
		}
	};
};

export function create(datas: any = {}): AppThunk {
	const postConfig = {
		data: datas,
		url: `plan`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await POST(postConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			dispatch(clearDetails());
			dispatch(getDetails());
			dispatch(toastFunction('Success', 'Plan created successfully', ''));
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
			dispatch(toastFunction('Error', apiErrorMessage(error, 'Failed to create plan'), ''));
		}
	};
}

export function update(datas: any = {}, planId: number): AppThunk {
	const putConfig = {
		data: datas,
		url: `plan/${planId}`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await PUT(putConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			dispatch(clearDetails());
			dispatch(getDetails());
			dispatch(toastFunction('Success', 'Plan updated successfully', ''));
		} catch (error) {
			dispatch(toastFunction('Error', apiErrorMessage(error, 'Failed to update plan'), ''));
			dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
		}
	};
}

export function deleteListItem(id = 0): AppThunk {
	const deleteConfig = { url: `plan/${id}` };
	return async function (dispatch) {
		try {
			const { status = 0, data = {} } = await DELETE(deleteConfig);
			if (data.message === 'PLAN_SUBSCRIPTION_EXISTS') {
				dispatch(
					toastFunction(
						'Error',
						'Unable to delete this plan. Organizations have already subscribed to this plan.',
						'',
					),
				);
			} else {
				dispatch({ type: ActionConstant.GET_STATUS, status });
				dispatch(clearDetails());
				dispatch(getDetails());
				dispatch(toastFunction('Success', 'Plan deleted successfully', ''));
			}
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 0 });
			dispatch(toastFunction('Error', 'Plan deletion failed', ''));
		}
	};
}

function subscriptionPlanReducer(state = initialState, action: ActionType): InitialState {
	switch (action.type) {
		case ActionConstant.GET_PLAN_LIST: {
			const { count = 0, rows = [] } = action.data;
			state.list = [];
			return {
				...state,
				list: [...state.list, ...rows],
				count,
				listFetching: false,
			};
		}
		case ActionConstant.CLEAR_DETAILS: {
			return {
				...state,
				list: [],
				count: 0,
				listFetching: true,
			};
		}
		case ActionConstant.GET_PLAN_LIST_FAILED: {
			return {
				...state,
				listFetching: false,
			};
		}
		case ActionConstant.GET_PLAN_DETAILS: {
			const { data } = action;
			return {
				...state,
				plan: data,
				detailsFetching: false,
			};
		}
		case ActionConstant.CLEAR_PLAN_DETAILS: {
			return {
				...state,
				plan: null,
				detailsFetching: true,
			};
		}
		case ActionConstant.GET_PLAN_DETAILS_FAILED: {
			return {
				...state,
				plan: null,
				detailsFetching: false,
			};
		}
		case ActionConstant.GET_STATUS: {
			return {
				...state,
				statusCode: action.status,
			};
		}
		case ActionConstant.CLEAR_STATUS: {
			return {
				...state,
				statusCode: 0,
			};
		}

		default:
			return state;
	}
}

export default subscriptionPlanReducer;
